import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebaseFunctions';
import type {
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  ResolveWorkflowTargetsRequest,
  ResolveWorkflowTargetsResponse,
  ResolveTableColumnValuesRequest,
  ResolveTableColumnValuesResponse,
  ResolveWorkflowRecipientPreviewRequest,
  ResolveWorkflowRecipientPreviewResponse,
  StopWorkflowRunRequest,
  StopWorkflowRunResponse,
} from '#types';

type ManageWorkflowRequest = {
  action: 'resetToDefault';
  workflowId: string;
};

type ManageWorkflowResponse = {
  success: boolean;
  workflowId: string;
};

/**
 * Client-side deadline for the executeWorkflow callable, in milliseconds.
 *
 * The callable runs with `timeoutSeconds: 540`. The Firebase JS SDK defaults to
 * 70s, which would abort the client while the function keeps running and
 * mutating — the "client saw failure, server succeeded" split. Sits just above
 * the server deadline so the server always loses the race.
 */
const EXECUTE_WORKFLOW_CLIENT_TIMEOUT_MS = 545_000;

export async function executeWorkflow(
  request: ExecuteWorkflowRequest
): Promise<ExecuteWorkflowResponse> {
  const call = httpsCallable<ExecuteWorkflowRequest, ExecuteWorkflowResponse>(
    functions,
    'executeWorkflow',
    { timeout: EXECUTE_WORKFLOW_CLIENT_TIMEOUT_MS }
  );
  const result = await call(request);
  return result.data;
}

/**
 * Records a stop request on the run the client minted the `runId` for. The engine
 * observes it at its next action boundary or recipient, so the run ends after the
 * step in progress rather than at the moment of the call. The SDK's default 70s
 * deadline is right here: the callable is a single read plus one transaction.
 */
export async function stopWorkflowRun(
  request: StopWorkflowRunRequest
): Promise<StopWorkflowRunResponse> {
  const call = httpsCallable<StopWorkflowRunRequest, StopWorkflowRunResponse>(
    functions,
    'stopWorkflowRun'
  );
  const result = await call(request);
  return result.data;
}

export async function resolveWorkflowTargets(
  request: ResolveWorkflowTargetsRequest
): Promise<ResolveWorkflowTargetsResponse> {
  const call = httpsCallable<ResolveWorkflowTargetsRequest, ResolveWorkflowTargetsResponse>(
    functions,
    'resolveWorkflowTargets'
  );
  const result = await call(request);
  return result.data;
}

export async function resolveTableColumnValues(
  request: ResolveTableColumnValuesRequest
): Promise<ResolveTableColumnValuesResponse> {
  const call = httpsCallable<ResolveTableColumnValuesRequest, ResolveTableColumnValuesResponse>(
    functions,
    'resolveTableColumnValues'
  );
  const result = await call(request);
  return result.data;
}

export async function resolveWorkflowRecipientPreview(
  request: ResolveWorkflowRecipientPreviewRequest
): Promise<ResolveWorkflowRecipientPreviewResponse> {
  const call = httpsCallable<ResolveWorkflowRecipientPreviewRequest, ResolveWorkflowRecipientPreviewResponse>(
    functions,
    'resolveWorkflowRecipientPreview'
  );
  const result = await call(request);
  return result.data;
}

export async function manageWorkflow(
  request: ManageWorkflowRequest
): Promise<ManageWorkflowResponse> {
  const call = httpsCallable<ManageWorkflowRequest, ManageWorkflowResponse>(
    functions,
    'manageWorkflow'
  );
  const result = await call(request);
  return result.data;
}
